import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { plannerService, eventService } from '../services';
import { useAuth } from '../context/AuthContext';
import useMediaQuery from '../utils/useMediaQuery';
import { safeBottom } from '../utils/safeArea';
import PortalLogo from '../components/PortalLogo';
import { Pill, Segmented, C } from '../components/portalkit';
import PlannerStyles from '../components/planner/plannerStyles';
import PlannerStepper from '../components/planner/PlannerStepper';
import PlannerLog from '../components/planner/PlannerLog';
import PlannerStage from '../components/planner/PlannerStage';
import PlanCards, { money } from '../components/planner/PlanCards';
import PlannerBillingModal from '../components/planner/PlannerBillingModal';
import PlannerBookModal from '../components/planner/PlannerBookModal';
import PosterStudio from '../components/planner/PosterStudio';
import SkipControls, { TogglePill } from '../components/planner/SkipControls';
import { EMPTY_SKIP, normalizeSkip, readSkips, sameSkip, anySkipped } from '../components/planner/plannerSkips';
import { WORKING, STEP_LABELS, stepOf, statusOf, logEntries, whenChip } from '../components/planner/plannerModel';

/*
 * The Party Planner — design_handoff_auth_profile_planner §5 (Sept 27 2026).
 *   /host/events/plan      the home: ONE LINE. WHOLE EVENT., the prompt card (mode, venue, prompt,
 *                          WHAT SHOULD IT HANDLE?, PLAN IT) and YOUR PLANS.
 *   /host/events/plan/:id  the workspace: prompt bar, the step rail, the animated stage, seven
 *                          cards, POSTER + LOG, and the sticky note bar (SEND NOTE · APPROVE / BOOK IT).
 * One black sticky header over both, with the lockup, a PLANNER pill, the status and EXIT PLANNER.
 *
 * The run is driven by the plan document polled every 3s; the beat on screen is paced so a stage
 * that closes in under 1.2s still gets its scene. The skip toggles are the one new thing here: the
 * flags go up with the prompt, a note that says "no permits" flips them mid-run, and every card can
 * bring its stage back with + PLAN IT ANYWAY.
 */

// The public event page itself, so the preview always matches the live design. Lazy: big, rare.
const EventApplyPage = lazy(() => import('./EventApplyPage'));

const TONE = { pink: C.pinkLight, green: C.green, held: '#D4A84B' };
const inputStyle = { height: 48, padding: '0 16px', borderRadius: 16, background: C.field, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const tinyLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.gray500, textTransform: 'uppercase' };

/* ─────────────────────────────── Header ─────────────────────────────── */
function PlannerHeader({ plan, view, phone, onExit }) {
  const wideish = useMediaQuery('(min-width: 560px)');
  const status = statusOf(plan);
  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 30, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, minHeight: phone ? 60 : 68, padding: `env(safe-area-inset-top) ${phone ? 14 : 28}px 0`, boxSizing: 'content-box', background: '#000', color: '#fff' }}>
      <a href="/host/events/plan" onClick={(e) => { e.preventDefault(); onExit('/host/events/plan'); }} className="pk-link-white" style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff', textDecoration: 'none' }}>
        <PortalLogo cap={24} />
        <span style={{ height: 26, padding: '0 12px', display: 'flex', alignItems: 'center', borderRadius: 999, background: C.surfaceDark, color: C.onBlack3, fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>PLANNER</span>
      </a>
      <span style={{ flex: 1 }} />
      {view === 'plan' && wideish && (
        <span data-testid="planner-status" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: TONE[status.tone] }}>
          <span data-motion="1" className={status.pulse ? 'pk-pulse' : undefined} aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: TONE[status.tone] }} />{status.label}
        </span>
      )}
      <Pill variant="secondary" on="black" size={40} onClick={() => onExit('/host')} style={{ padding: '0 16px', fontSize: 11 }}>{view === 'plan' && !phone ? 'EXIT PLANNER' : 'EXIT'}</Pill>
    </header>
  );
}

/* ─────────────────────────────── The prompt screen ─────────────────────────────── */
function PromptScreen({ onCreated, billing, onUpgrade, initial = null, phone }) {
  // A held plan hands its prompt back here so the host can edit it and try again.
  const [prompt, setPrompt] = useState(initial?.prompt || '');
  const [mode, setMode] = useState(initial?.venueMode || 'find');
  const [own, setOwn] = useState({ name: '', address: '', costCents: 0, capacity: '', isPrivateResidence: false, showNamePublicly: false, isLicensed: false });
  const [skip, setSkip] = useState(() => readSkips(initial?.prompt || '', EMPTY_SKIP));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onPrompt = (v) => { setPrompt(v); setSkip((s) => readSkips(v, s)); };
  const ready = prompt.trim().length >= 8;

  const submit = async (e) => {
    e?.preventDefault();
    if (!ready) { setError('Tell us a little more about the event.'); return; }
    setBusy(true); setError(null);
    const r = await plannerService.create({
      prompt: prompt.trim(), venueMode: mode === 'have' ? 'own' : 'find',
      ownVenue: mode === 'have' ? { ...own, capacity: own.capacity ? parseInt(own.capacity, 10) : null, costCents: Math.round(Number(own.costCents) || 0) } : null,
      skip: anySkipped(skip) ? normalizeSkip(skip) : null,
    });
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not start the plan.'); return; }
    onCreated(r.data);
  };

  const field = (key, label, extra = {}) => (
    <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={tinyLabel}>{label}</span>
      <input className="pk-in pk-in-light" style={inputStyle} value={extra.value ?? own[key]} onChange={(e) => (extra.onChange ? extra.onChange(e) : setOwn({ ...own, [key]: e.target.value }))} placeholder={extra.placeholder} inputMode={extra.inputMode} type={extra.type} min={extra.min} />
    </label>
  );

  return (
    <main style={{ width: '100%', maxWidth: 920, margin: '0 auto', padding: phone ? '24px 16px 48px' : '48px 28px 80px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: phone ? 28 : 40, fontWeight: 800, letterSpacing: '.06em', lineHeight: 1.05, textWrap: 'balance' }}>ONE LINE. WHOLE EVENT.</h1>
        <p style={{ margin: 0, maxWidth: 680, fontSize: 14, lineHeight: 1.65, color: C.gray600, textWrap: 'pretty' }}>Say who, how many, where, when, and what kind of night. We find the room, the sound, the crew, the permits, and the price.</p>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: phone ? 14 : 20, borderRadius: 28, background: '#fff' }}>
        <Segmented on="white" ring={false} grow={false} height={36} fontSize={11} tracking=".14em" style={{ alignSelf: 'flex-start' }} label="Where it happens"
          tabs={[{ key: 'find', label: 'FIND ME A SPACE' }, { key: 'have', label: 'I HAVE A SPACE' }]} value={mode} onChange={setMode} />

        {mode === 'have' && (
          <div className="pk-rise-fast" data-testid="own-venue-fields" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, ...inputStyle, padding: '0 16px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></svg>
              <input className="pk-in pk-in-light" value={own.name} onChange={(e) => setOwn({ ...own, name: e.target.value })} placeholder="Where is it? Venue name or address" aria-label="Venue name or address" style={{ flex: 1, fontSize: 14 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(3, minmax(0,1fr))', gap: 10 }}>
              {field('address', 'ADDRESS (PRIVATE UNTIL GREENLIT)', { placeholder: 'Street, city' })}
              {field('costCents', 'COST ($, 0 IF IT’S YOURS)', { inputMode: 'decimal', placeholder: '0', value: own.costCents ? own.costCents / 100 : '', onChange: (e) => setOwn({ ...own, costCents: Math.round((parseFloat(e.target.value) || 0) * 100) }) })}
              {field('capacity', 'CAPACITY', { type: 'number', min: '1', placeholder: '40' })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[['isPrivateResidence', 'PRIVATE RESIDENCE'], ['isLicensed', 'LICENSED VENUE'], ['showNamePublicly', 'SHOW THE NAME PUBLICLY']].map(([k, label]) => (
                <TogglePill key={k} label={label} on={!!own[k]} onClick={() => setOwn({ ...own, [k]: !own[k] })} />
              ))}
            </div>
            {own.isPrivateResidence && <p style={{ margin: 0, fontSize: 11, color: C.gray500 }}>A home is never shown publicly, even if you tick the box.</p>}
          </div>
        )}

        <textarea className="pk-in pk-in-light pk-textarea" value={prompt} onChange={(e) => onPrompt(e.target.value)} rows={4} maxLength={2000} aria-label="Your prompt"
          placeholder="Rooftop birthday for ~80 in Kelowna, Sat Oct 18, house music, open bar till midnight, I want to break even at $40."
          style={{ width: '100%', boxSizing: 'border-box', padding: '16px 18px', borderRadius: 18, fontSize: 15, lineHeight: 1.6, resize: 'vertical', color: '#000' }} />

        <SkipControls skip={skip} onChange={setSkip} disabled={busy} />

        {error && <p role="alert" style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: '#000', background: C.held, borderRadius: 999, padding: '8px 14px', alignSelf: 'flex-start' }}>{error}</p>}

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px' }}>
          <Pill type="submit" variant="primary" on="white" size={48} disabled={!ready || busy} style={{ opacity: !ready && !busy ? 0.3 : 1 }}>{busy ? 'STARTING…' : '[ PLAN IT ]'}</Pill>
          {billing && !billing.isAdmin && (
            <span style={{ ...tinyLabel, letterSpacing: '.12em' }}>
              {billing.effectiveTier === 'free' ? 'FREE: FIRST PLAN ON US · POSTER ON APPROVE' : `${billing.effectiveTier.replace('_', ' ').toUpperCase()} · ${money(billing.usedCents)} OF ${money(billing.monthlyBudgetCents || 0)} THIS MONTH`}
              {' · '}<button type="button" onClick={onUpgrade} className="pk" style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', letterSpacing: 'inherit' }}>{billing.effectiveTier === 'free' ? 'UPGRADE' : 'CHANGE TIER'}</button>
            </span>
          )}
          {billing?.isAdmin && <span style={tinyLabel}>ADMIN · ULTIMATE · NO MONTHLY CAP</span>}
        </div>
      </form>

      <PlansList />
    </main>
  );
}

/* ─────────────────────────── Your previous plans ─────────────────────────── */
const PLAN_PILL = {
  approved: ['APPROVED', C.greenBg, C.greenFg], booked: ['BOOKED', C.greenBg, C.greenFg], ready: ['READY', '#000', '#fff'],
  failed: ['HELD', C.held, '#000'], budget_exhausted: ['HELD', C.held, '#000'], archived: ['ARCHIVED', C.field, C.gray400],
};
const planPill = (s) => PLAN_PILL[s] || ['PLANNING…', C.field, C.gray500, true];
const planDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase(); } catch { return ''; } };

function PlansList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  useEffect(() => { (async () => { const r = await plannerService.mine(); setPlans(r?.success ? (r.data || []) : []); })(); }, []);
  if (!plans || plans.length === 0) return null;
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="your-plans">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>YOUR PLANS</span>
        <span style={{ flex: 1, height: 2, background: '#000' }} />
        <span style={tinyLabel}>{plans.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {plans.map((p) => {
          const [label, bg, fg, working] = planPill(p.status);
          return (
            <div key={p.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: '16px 16px 16px 20px', borderRadius: 22, background: '#fff' }}>
              <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>{p.title || p.prompt || 'UNTITLED PLAN'}</span>
                  <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px', borderRadius: 999, background: bg, color: fg, fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>
                    {working && <span data-motion="1" className="pk-pulse" aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />}{label}
                  </span>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.12em', color: C.gray400, textTransform: 'uppercase' }}>{[p.regionName, planDate(p.updatedAt)].filter(Boolean).join(' · ')}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {p.eventId && <Pill variant="secondary" on="white" size={38} onClick={() => navigate(`/host/events/${p.eventId}/edit`)}>EVENT</Pill>}
                <Pill variant="primary" on="white" size={38} weight={700} onClick={() => navigate(`/host/events/plan/${p.id}`)}>REVIEW →</Pill>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────── The live run ─────────────────────────────── */

/**
 * The beat on screen trails the plan by at most one step per 1.2s, so fast stages don't collapse.
 * Until the plan has loaded (`primed`) it simply follows: opening a plan mid-run lands on its
 * current beat rather than replaying the ones already played.
 */
function usePacedStep(target, reduced, primed) {
  const [shown, setShown] = useState(target);
  const wasPrimed = useRef(false);
  useEffect(() => {
    if (!primed || !wasPrimed.current) { wasPrimed.current = primed; if (target !== shown) setShown(target); return undefined; }
    if (reduced || target <= shown) { if (target !== shown) setShown(target); return undefined; }
    const t = setTimeout(() => setShown((s) => Math.min(target, s + 1)), 1200);
    return () => clearTimeout(t);
  }, [target, shown, reduced, primed]);
  return shown;
}

function LiveRun({ planId, billing, onUpgrade, onExit, onPlan, phone }) {
  const navigate = useNavigate();
  const wide = useMediaQuery('(min-width: 1180px)');
  const cards3 = useMediaQuery('(min-width: 1000px)');
  const cards2 = useMediaQuery('(min-width: 600px)');
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [crew, setCrew] = useState({ bartenders: 0, servers: 0, security: 0 });
  const [approvedPath, setApprovedPath] = useState(null);
  const [posterBusy, setPosterBusy] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  const [preview, setPreview] = useState(null); // { dto, error } while open
  const [skipLocal, setSkipLocal] = useState(null); // optimistic toggles while a save is in flight

  const load = useCallback(async () => {
    const r = await plannerService.get(planId);
    if (!r?.success) { setError(r?.error || 'Plan not found.'); return null; }
    setPlan(r.data); return r.data;
  }, [planId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { onPlan?.(plan); }, [plan, onPlan]);
  useEffect(() => {
    if (!plan || !WORKING.has(plan.status)) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [plan, load]);

  const doc = plan?.plan;
  const skip = skipLocal || normalizeSkip(doc?.skip);
  useEffect(() => { if (skipLocal && sameSkip(skipLocal, doc?.skip)) setSkipLocal(null); }, [doc?.skip, skipLocal]);

  // Crew figures tick in one at a time (90 ms) so the room and the counter stay in lockstep.
  const target = useMemo(() => {
    const s = doc?.staff || [];
    if (!doc?.stages?.staff || skip.staff) return { bartenders: 0, servers: 0, security: 0 };
    return { bartenders: s.find((r) => r.role === 'bartender')?.count || 0, servers: s.find((r) => r.role === 'servers')?.count || 0, security: s.find((r) => r.role === 'security')?.count || 0 };
  }, [doc, skip.staff]);
  useEffect(() => {
    if (reduced) { setCrew(target); return undefined; }
    const t = setInterval(() => setCrew((c) => {
      if (c.bartenders < target.bartenders) return { ...c, bartenders: c.bartenders + 1 };
      if (c.servers < target.servers) return { ...c, servers: c.servers + 1 };
      if (c.security < target.security) return { ...c, security: c.security + 1 };
      if (c.bartenders > target.bartenders || c.servers > target.servers || c.security > target.security) return target;
      return c;
    }), 90);
    return () => clearInterval(t);
  }, [target, reduced]);

  const working = !!plan && WORKING.has(plan.status);
  const stages = doc?.stages || {};
  const approved = plan?.status === 'approved' || plan?.status === 'booked';
  const ready = plan?.status === 'ready' || approved;
  const failed = plan?.status === 'failed';
  const budgetOut = plan?.status === 'budget_exhausted';
  const step = usePacedStep(stepOf(plan), reduced, !!plan);
  const shownReady = ready && step >= 8;
  const percent = shownReady ? 100 : Math.round(((step - 1) / 8) * 100);
  const posterLocked = !plan?.posterOnDraft && !approved;
  const progressLabel = plan?.status === 'booked' ? 'BOOKED' : approved ? 'APPROVED' : shownReady ? (plan?.posterUrl ? 'POSTER DRAWN' : 'PLAN READY') : failed ? 'HELD' : budgetOut ? 'BUDGET USED UP' : `${STEP_LABELS[Math.min(step, 8) - 1]}…`;
  const entries = useMemo(() => logEntries(plan), [plan]);
  const intent = doc?.intent || {};
  const chosen = (doc?.venues || []).find((v) => v.id === doc?.chosenVenueId);
  const posterMeta = [whenChip(intent.startLocal), chosen?.name || doc?.regionName].filter(Boolean).join(' · ');

  const act = async (fn) => { setBusy(true); const r = await fn(); setBusy(false); if (!r.success) setError(r.error || 'That didn’t work.'); else { setError(null); setPlan(r.data); } return r; };
  const setSkip = async (next) => {
    const n = normalizeSkip(next);
    if (sameSkip(n, skip)) return;
    setSkipLocal(n);
    const r = await act(() => plannerService.setSkip(planId, n));
    if (!r.success) setSkipLocal(null);
  };
  const sendNote = async (e) => {
    e?.preventDefault();
    if (note.trim().length < 2) return;
    const n = note.trim(); setNote('');
    // "no permits" in a note is an instruction: flip the toggle as well as revising.
    const next = readSkips(n, skip);
    if (!sameSkip(next, skip)) await setSkip(next);
    await act(() => plannerService.revise(planId, n));
  };
  const approve = async () => { const r = await act(() => plannerService.approve(planId)); if (r.success) { setApprovedPath(r.data.eventEditPath || null); setOverlayDismissed(false); } };
  const regenPoster = async (prompt) => { setPosterBusy(true); const r = await plannerService.regeneratePoster(planId, prompt); setPosterBusy(false); if (r.success) { setError(null); setPlan(r.data); } else setError(r.error || 'The poster couldn’t be drawn.'); };
  const savePosterPrompt = (prompt) => act(() => plannerService.setPosterPrompt(planId, prompt));
  const uploadPosterReference = async (file) => { setPosterBusy(true); const r = await plannerService.setPosterReference(planId, file); setPosterBusy(false); if (r.success) { setError(null); setPlan(r.data); } else setError(r.error || 'That image couldn’t be added.'); };
  const clearPosterReference = () => act(() => plannerService.clearPosterReference(planId));
  const posterStudioProps = { plan, drawing: posterBusy, busy, onDraw: regenPoster, onSavePrompt: savePosterPrompt, onUploadReference: uploadPosterReference, onClearReference: clearPosterReference };
  // BOOK IT asks first: real offers and real emails go out on the other side of this, so the
  // modal names everyone who would be contacted before any of it happens.
  const book = () => setBookOpen(true);
  const cancelApproval = () => act(() => plannerService.cancelApproval(planId));
  const openPreview = async () => {
    if (!plan?.eventId) return;
    setPreview({ dto: null, error: null });
    const r = await eventService.getEvent(plan.eventId);
    setPreview(r?.success ? { dto: r.data, error: null } : { dto: null, error: r?.error || 'Could not load the event.' });
  };
  const revertTo = (v) => act(() => plannerService.revertTo(planId, v));
  const choose = (payload) => act(() => plannerService.choose(planId, payload));
  const permit = (name, st) => act(() => plannerService.setPermit(planId, name, st));

  const notePh = ready ? 'Change anything before you book…' : 'Add a note while it works…';
  const primary = plan?.status === 'booked'
    ? { label: 'BOOKED ✓', bg: C.green, disabled: true }
    : plan?.status === 'approved'
      ? { label: 'BOOK IT', bg: '#000', disabled: busy, onClick: book }
      : { label: phone ? 'APPROVE' : 'APPROVE PLAN', bg: '#000', disabled: busy || plan?.status !== 'ready', onClick: approve, dim: plan?.status !== 'ready' };

  return (
    <div className={`pk ${reduced ? 'pl-reduced' : ''}`} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <main style={{ flex: 1, width: '100%', maxWidth: 1560, margin: '0 auto', padding: phone ? '12px 12px 8px' : '18px 28px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Prompt bar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px', borderRadius: 22, background: '#fff' }}>
          <span style={{ flexShrink: 0, paddingTop: 3, fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: C.gray400 }}>YOUR PROMPT</span>
          <span style={{ fontSize: 14, lineHeight: 1.55 }}>{plan?.prompt || '…'}</span>
        </div>

        <PlannerStepper stages={stages} skip={skip} step={step} ready={shownReady} posterLocked={posterLocked} />

        <div style={{ display: 'grid', gridTemplateColumns: wide ? 'minmax(0,1fr) 400px' : 'minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <PlannerStage step={step} phone={phone} skip={skip} doc={doc} plan={plan} crew={crew} failed={failed} reduced={reduced} />

            {(failed || budgetOut) && (
              <div data-testid="held" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 18, borderRadius: 24, background: '#fff' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: C.gray500 }}>{failed ? 'PLAN HELD' : 'BUDGET USED UP'}</span>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>{plan.lastError || doc?.failureReason}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {failed && <Pill variant="primary" on="white" size={38} weight={700} onClick={() => navigate('/host/events/plan', { state: { prompt: plan?.prompt, venueMode: plan?.venueMode } })}>EDIT PROMPT & TRY AGAIN</Pill>}
                  {failed && <Pill variant="secondary" on="white" size={38} onClick={() => act(() => plannerService.revise(planId, 'I have my own venue — use my space instead of finding one.'))}>ADD YOUR OWN VENUE</Pill>}
                  {budgetOut && !billing?.isAdmin && <Pill variant="primary" on="white" size={38} weight={700} onClick={onUpgrade}>UPGRADE</Pill>}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: cards3 ? 'repeat(3, minmax(0,1fr))' : cards2 ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)', gap: 12, minWidth: 0 }}>
              <PlanCards plan={plan} doc={doc} step={step} ready={shownReady} skip={skip} onChoose={choose} onPermit={permit} onUnskip={(key) => setSkip({ ...skip, [key]: false })} busy={busy || working} posterOnDraft={plan?.posterOnDraft && working} />
            </div>

            {/* The poster prompt, before anything is drawn — so it can be changed rather than discovered. */}
            {(plan?.status === 'ready' || approved) && !skip.poster && <PosterStudio {...posterStudioProps} />}

            {plan?.versions?.length > 1 && (
              <div data-testid="drafts" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 18, borderRadius: 24, background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: C.gray500 }}>DRAFTS</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: C.gray400 }}>EVERY FINISHED RUN · PUT ANY ONE BACK</span>
                </div>
                {plan.versions.map((v) => (
                  <div key={v.version} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 12, background: C.inside }}>
                    <span style={{ fontSize: 11, fontWeight: 700, flex: '0 0 auto' }}>DRAFT {v.version}</span>
                    <span style={{ fontSize: 11, color: C.gray500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.label || '—'}</span>
                    {v.isCurrent
                      ? <span style={{ height: 22, padding: '0 9px', display: 'flex', alignItems: 'center', borderRadius: 999, background: C.greenBg, color: C.greenFg, fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>CURRENT</span>
                      : <Pill variant="secondary" on="white" size={28} fontSize={9} onClick={() => revertTo(v.version)} disabled={busy || working} data-testid={`revert-${v.version}`}>REVERT</Pill>}
                  </div>
                ))}
              </div>
            )}
            {!plan?.posterUrl && !approved && posterLocked && !billing?.isAdmin && plan?.status === 'ready' && !skip.poster && (
              <button type="button" onClick={onUpgrade} className="pk" style={{ alignSelf: 'flex-start', padding: 0, border: 'none', background: 'none', fontSize: 10, letterSpacing: '.05em', color: C.gray500, textDecoration: 'underline', cursor: 'pointer' }}>SEE THE POSTER NOW → PARTY PLANNER ULTIMATE</button>
            )}
          </div>

          <div style={{ position: wide ? 'sticky' : 'static', top: 88, minWidth: 0 }}>
            <PlannerLog entries={entries} working={working} ready={shownReady} posterUrl={skip.poster ? null : plan?.posterUrl || null} title={doc?.title || intent.title} meta={posterMeta} progressLabel={progressLabel} percent={percent} reduced={reduced} aspect={wide || phone ? '4 / 5' : '16 / 9'} onPreview={openPreview} previewEnabled={!!plan?.eventId} />
          </div>
        </div>
      </main>

      {/* Note bar */}
      <div style={{ position: 'sticky', bottom: 0, zIndex: 20, padding: `12px ${phone ? 14 : 28}px calc(16px + env(safe-area-inset-bottom))`, background: 'linear-gradient(to bottom, rgba(245,245,245,0), #F5F5F5 30%)' }}>
        <form onSubmit={sendNote} style={{ maxWidth: 1560, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 8, padding: 6, borderRadius: 999, background: '#fff', boxShadow: '0 0 0 2px #000' }}>
          <input className="pk-in pk-in-light" value={note} onChange={(e) => setNote(e.target.value)} placeholder={notePh} aria-label="Add a note" disabled={failed || busy} style={{ flex: 1, height: 44, padding: '0 16px', fontSize: 13, letterSpacing: '.04em' }} />
          <Pill type="submit" variant="secondary" on="white" size={44} disabled={busy || failed || note.trim().length < 2} style={{ flexShrink: 0, opacity: note.trim().length < 2 ? 0.4 : 1 }}>{phone ? 'SEND' : 'SEND NOTE'}</Pill>
          {plan?.status === 'approved' && <Pill variant="secondary" on="white" size={44} onClick={cancelApproval} disabled={busy} data-testid="cancel-approval" style={{ flexShrink: 0 }}>{phone ? 'CANCEL' : 'CANCEL APPROVAL'}</Pill>}
          <button type="button" onClick={primary.onClick} disabled={primary.disabled} className="pk pk-btn" style={{ flexShrink: 0, height: 44, padding: '0 20px', background: primary.bg, color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '.16em', opacity: primary.dim ? 0.3 : 1 }}>{primary.label}</button>
        </form>
        {plan?.budget && !plan.budget.isAdmin && plan.budget.budgetCents != null && !phone && (
          <div style={{ maxWidth: 1560, margin: '6px auto 0', textAlign: 'right', fontSize: 9, letterSpacing: '.05em', color: C.gray500 }}>THIS MONTH: {money(plan.budget.usedCents)} OF {money(plan.budget.budgetCents)}</div>
        )}
      </div>
      {error && <div role="alert" style={{ position: 'fixed', bottom: safeBottom(96), left: '50%', transform: 'translateX(-50%)', zIndex: 40, padding: '8px 14px', borderRadius: 999, background: '#000', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', maxWidth: 'calc(100vw - 32px)' }}>{error}</div>}

      {/* Approved — review the poster (draw / redraw it here), then step into the event draft. */}
      {approved && !overlayDismissed && (
        <div className="safe-inset" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.6)', padding: 16 }} role="dialog" aria-modal="true" aria-label="Plan approved">
          <div style={{ width: '100%', maxWidth: 460, maxHeight: '100%', overflowY: 'auto', borderRadius: 24, background: '#fff', display: 'flex', flexDirection: 'column', gap: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '.08em' }}>PLAN APPROVED</span>
              <span style={{ height: 22, padding: '0 9px', display: 'flex', alignItems: 'center', borderRadius: 999, background: plan?.posterUrl ? C.greenBg : C.field, color: plan?.posterUrl ? C.greenFg : C.gray500, fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>{plan?.posterUrl ? 'POSTER DRAWN' : 'NO POSTER'}</span>
            </div>
            {!skip.poster && <PosterStudio {...posterStudioProps} />}
            <p style={{ margin: 0, fontSize: 12, color: C.gray500, lineHeight: 1.55 }}>Your event draft is ready with {skip.poster ? 'the description' : 'this poster, the description'} and everything the plan found. Review it, tweak anything, then publish when you’re happy. To change the plan itself, close this and add a note — then approve again to republish.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
              <Pill variant="secondary" on="white" size={40} onClick={() => setOverlayDismissed(true)}>LATER</Pill>
              {plan?.eventId && <Pill variant="secondary" on="white" size={40} onClick={openPreview}>PREVIEW ↗</Pill>}
              <Pill variant="primary" on="white" size={40} weight={700} onClick={() => navigate(approvedPath || plan?.eventEditPath || '/host')}>GO TO YOUR EVENT →</Pill>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW — the real public event page, embedded, so it always matches the live design. */}
      {preview && (
        <div className="safe-inset" style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column', background: '#fff' }} role="dialog" aria-modal="true" aria-label="Event preview" data-testid="event-preview">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px', background: '#000', color: '#fff', flexShrink: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em' }}>PREVIEW · HOW YOUR EVENT WILL LOOK</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!phone && <span style={{ fontSize: 10, letterSpacing: '.05em', color: C.gray400 }}>NOT LIVE UNTIL YOU PUBLISH</span>}
              <Pill variant="primary" on="black" size={34} weight={700} onClick={() => setPreview(null)}>CLOSE</Pill>
            </span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {preview.error && <div style={{ padding: 24, fontSize: 12, color: '#DC2626', textTransform: 'uppercase' }}>{preview.error}</div>}
            {!preview.dto && !preview.error && <div style={{ padding: 24, fontSize: 12, color: C.gray500, letterSpacing: '.05em' }}>BUILDING PREVIEW…</div>}
            {preview.dto && (
              <Suspense fallback={<div style={{ padding: 24, fontSize: 12, color: C.gray500, letterSpacing: '.05em' }}>BUILDING PREVIEW…</div>}>
                <EventApplyPage previewEvent={preview.dto} embedded />
              </Suspense>
            )}
          </div>
        </div>
      )}
      <PlannerBookModal isOpen={bookOpen} planId={planId} onClose={() => setBookOpen(false)} onBooked={setPlan} />
      {onExit && null}
    </div>
  );
}

/* ─────────────────────────────── Page ─────────────────────────────── */
export default function PlannerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const phone = useMediaQuery('(max-width: 699px)');
  const [billing, setBilling] = useState(null);
  const [billingOpen, setBillingOpen] = useState(params.get('billing') === '1');
  const [plan, setPlan] = useState(null);

  const loadBilling = useCallback(async () => { const r = await plannerService.billing(); if (r.success) setBilling(r.data); }, []);
  useEffect(() => { if (isAuthenticated) loadBilling(); }, [isAuthenticated, loadBilling]);
  useEffect(() => { if (!id) setPlan(null); }, [id]);

  const exit = (to = '/host') => navigate(to);

  return (
    <div className="pk" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: C.inside, color: '#000' }}>
      <PlannerStyles />
      <PlannerHeader plan={plan} view={id ? 'plan' : 'home'} phone={phone} onExit={exit} />
      {id
        ? <LiveRun key={id} planId={id} billing={billing} onUpgrade={() => setBillingOpen(true)} onExit={exit} onPlan={setPlan} phone={phone} />
        : <PromptScreen billing={billing} onUpgrade={() => setBillingOpen(true)} initial={location.state || null} onCreated={(p) => navigate(`/host/events/plan/${p.id}`)} phone={phone} />}
      <PlannerBillingModal isOpen={billingOpen} onClose={() => setBillingOpen(false)} billing={billing} onDone={loadBilling} />
    </div>
  );
}
